import { EventsHub, deferred } from 'wootils/esm/shared';
/**
 * This services handles the data of notifications, alert windows and confirmation propmpts.
 * The service also provides with listener so components can show everything on the screen.
 * @extends {EventsHub}
 * @ngesdoc service
 * @ngname uimessages
 * @ngmodule common
 */
class UIMessages extends EventsHub {
  /**
   * @param {ngInterval} $interval To validate the expiration time of notifications.
   * @param {ngQ}        $q        To reject promises when a confirmation prompts gets cancelled.
   * @param {ngSCE}      $sce      To generate HTML strings that can be used on a component.
   */
  constructor($interval, $q, $sce) {
    'ngInject';

    super();
    /**
     * A local reference for the `$interval` service.
     * @type {ngInterval}
     */
    this.$interval = $interval;
    /**
     * A local reference for the `$q` service.
     * @type {ngQ}
     */
    this.$q = $q;
    /**
     * A local reference for the `$sce` service.
     * @type {ngSCE}
     */
    this.$sce = $sce;
    /**
     * The default options for alert windows and confirmation prompts.
     * @type {UIMessagesPopupOptions}
     */
    this.popupInfoBaseOptions = {
      title: null,
      message: null,
      deferred: null,
      confirm: false,
      confirmText: 'Accept',
      cancelText: 'Cancel',
      destructive: false,
      html: false,
    };
    /**
     * The information for the alert/prompt that is currently active.
     * @type {UIMessagesPopupOptions}
     */
    this.popupInfo = {};
    /**
     * An incremental ID to add to the notifications when they are created.
     * @type {Number}
     */
    this._currentNotificationId = 0;
    /**
     * A dictionary with the different events the service can trigger.
     * @type {Object}
     */
    this._eventsNames = {
      alert: {
        new: 'app/services/uimessages/alert/new',
        accepted: 'app/services/uimessages/alert/accepted',
        closed: 'app/services/uimessages/alert/closed',
      },
      confirm: {
        new: 'app/services/uimessages/confirm/new',
        accepted: 'app/services/uimessages/confirm/accepted',
        canceled: 'app/services/uimessages/confirm/canceled',
        closed: 'app/services/uimessages/confirm/closed',
      },
      notifications: {
        new: 'app/services/uimessages/notifications/new',
        expired: 'app/services/uimessages/notifications/expired',
      },
    };
  }
  /**
   * Creates an alert window.
   * @param {string}                 title        The alert title.
   * @param {string}                 content      The alert message.
   * @param {UIMessagesPopupOptions} [options={}] Options to customize the alert.
   * @return {Promise<undefined,Error>}
   */
  alert(title, content, options = {}) {
    return this._popup(
      title,
      content,
      Object.assign({ confirm: false }, options),
      'Another alert window is already being shown',
      this._eventsNames.alert.new,
    )
    .then(() => {
      this.emit(this._eventsNames.alert.accepted);
      this.emit(this._eventsNames.alert.closed);
    });
  }
  /**
   * Creates a confirmation prompt.
   * @param {string}                 title        The prompt title.
   * @param {string}                 content      The prompt message.
   * @param {UIMessagesPopupOptions} [options={}] Options to customize the prompt.
   * @return {Promise<boolean,Error>}
   */
  confirm(title, content, options = {}) {
    return this._popup(
      title,
      content,
      Object.assign({ confirm: true }, options),
      'Another confirmation prompt is already being shown',
      this._eventsNames.confirm.new,
    )
    .then((response) => {
      if (response) {
        this.emit(this._eventsNames.confirm.accepted, response);
      } else {
        this.emit(this._eventsNames.confirm.canceled, response);
      }

      this.emit(this._eventsNames.confirm.closed);
      return response;
    });
  }
  /**
   * Create a new notification.
   * @param {string}                         content      The notification message.
   * @param {UIMessagesNotificationsOptions} [options={}] Options to customize the notification.
   */
  notification(content, options = {}) {
    this._currentNotificationId++;
    const notification = Object.assign({
      id: this._currentNotificationId,
      type: 'success',
      expiration: 3000,
      html: false,
      closeButton: true,
      content,
    }, options);

    if (notification.html) {
      notification.content = this.$sce.trustAsHtml(notification.content);
    }

    this.emit(this._eventsNames.notifications.new, notification);
    if (notification.expiration) {
      let timer = this.$interval(() => {
        this.emit(this._eventsNames.notifications.expired, notification);
        this.$interval.cancel(timer);
        timer = null;
      }, notification.expiration);
    }
  }
  /**
   * Adds a listener for when an alert is _"accepted"_.
   * @param  {Function} fn The function to be triggered when the alert is _"accepted"_.
   * @return {Function} An unsubscribe function to remove the listener.
   */
  onAcceptedAlert(fn) {
    return this.on(this._eventsNames.alert.accepted, fn);
  }
  /**
   * Adds a listener for when the user _"accepts"_ the confirmation prompt.
   * @param  {Function} fn The function to be triggered when the prompt is _"accepted"_.
   * @return {Function} An unsubscribe function to remove the listener.
   */
  onAcceptedConfirm(fn) {
    return this.on(this._eventsNames.confirm.accepted, fn);
  }
  /**
   * Adds a listener for when the user _"cancels"_ the confirmation prompt.
   * @param  {Function} fn The function to be triggered when the prompt is _"cancelled"_.
   * @return {Function} An unsubscribe function to remove the listener.
   */
  onCanceledConfirm(fn) {
    return this.on(this._eventsNames.confirm.canceled, fn);
  }
  /**
   * Adds a listener for when an alert is closed.
   * @param  {Function} fn The function to be triggered when the alert is closed.
   * @return {Function} An unsubscribe function to remove the listener.
   */
  onClosedAlert(fn) {
    return this.on(this._eventsNames.alert.closed, fn);
  }
  /**
   * Adds a listener for when a confirmation prompt is closed.
   * @param  {Function} fn The function to be triggered when the prompt is closed.
   * @return {Function} An unsubscribe function to remove the listener.
   */
  onClosedConfirm(fn) {
    return this.on(this._eventsNames.confirm.closed, fn);
  }
  /**
   * Adds a listener for when a notification expires.
   * @param  {Function} fn The function to be triggered when the notification expires.
   * @return {Function} An unsubscribe function to remove the listener.
   */
  onExpiredNotification(fn) {
    return this.on(this._eventsNames.notifications.expired, fn);
  }
  /**
   * Adds a listener for when an new alert is created.
   * @param  {Function} fn The function to be triggered when the alert is created.
   * @return {Function} An unsubscribe function to remove the listener.
   */
  onNewAlert(fn) {
    return this.on(this._eventsNames.alert.new, fn);
  }
  /**
   * Adds a listener for when an new confirmation prompt is created.
   * @param  {Function} fn The function to be triggered when the prompt is created.
   * @return {Function} An unsubscribe function to remove the listener.
   */
  onNewConfirm(fn) {
    return this.on(this._eventsNames.confirm.new, fn);
  }
  /**
   * Adds a listener for when a new notification is created.
   * @param  {Function} fn The function to be triggered when the notification is created.
   * @return {Function} An unsubscribe function to remove the listener.
   */
  onNewNotification(fn) {
    return this.on(this._eventsNames.notifications.new, fn);
  }
  /**
   * Helper method to create alerts and confirmation prompts as popups.
   * @param {string}                 title        The popup title.
   * @param {string}                 content      The popup contents.
   * @param {UIMessagesPopupOptions} options      The popup custom options.
   * @param {string}                 errorMessage An error message to reject the promise in case
   *                                              there's another popup currently active.
   * @param {string}                 eventName    The _"new event"_ for the type of popup the
   *                                              method is creating.
   * @return {Promise<?boolean,Error>} If the popup is a confirmation prompt, it will resolve with
   *                                   a boolean value, but if it's an alert, it won't send any
   *                                   parameter.
   */
  _popup(title, content, options, errorMessage, eventName) {
    let result;
    if (this.popupInfo.deferred) {
      result = this.$q.reject(new Error(errorMessage));
    } else {
      const defer = deferred();
      this.popupInfo = Object.assign({}, this.popupInfoBaseOptions, {
        deferred: defer,
        title,
        content,
      }, options);
      if (this.popupInfo.html) {
        this.popupInfo.content = this.$sce.trustAsHtml(this.popupInfo.content);
      }

      this.emit(eventName, this.popupInfo);
      result = defer.promise
      .then((response) => {
        this.popupInfo.deferred = null;
        return response;
      });
    }

    return result;
  }
}

export default UIMessages;
